import { cn } from "@/lib/utils";
import { Check, Money } from "@phosphor-icons/react";
import { getInitials } from "./payout-helpers";

export function PayoutAvatar({
  name,
  tone = "muted",
  className,
}: {
  name: string;
  tone?: "muted" | "primary";
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex size-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
        tone === "primary"
          ? "bg-primary/10 text-primary"
          : "bg-muted text-muted-foreground",
        className,
      )}
    >
      {getInitials(name)}
    </div>
  );
}

export function SelectCheckbox({
  checked,
  onToggle,
  label,
  className,
}: {
  checked: boolean;
  onToggle: () => void;
  label: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={cn(
        "flex size-[18px] shrink-0 items-center justify-center rounded-[5px] border-2 transition-all",
        checked
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card hover:border-primary/50",
        className,
      )}
    >
      {checked && <Check weight="bold" className="size-3" />}
    </button>
  );
}

/**
 * Pill-shaped payout action: a coin badge that tips over and the whole pill
 * fills with the brand petrolej on hover.
 */
export function PayButton({
  onClick,
  children = "Isplati",
  className,
}: {
  onClick: () => void;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-primary/25 bg-card pl-1.5 pr-3 text-[11px] font-semibold text-primary transition-all duration-200",
        "hover:border-primary hover:bg-primary hover:text-primary-foreground hover:shadow-md hover:shadow-primary/25",
        "active:scale-[0.96]",
        className,
      )}
    >
      <span className="flex size-[18px] items-center justify-center rounded-full bg-primary/10 transition-all duration-200 group-hover:rotate-12 group-hover:bg-white/20">
        <Money className="size-3" weight="bold" />
      </span>
      {children}
    </button>
  );
}

export function StatCard({
  label,
  value,
  suffix,
  tone = "neutral",
}: {
  label: string;
  value: string;
  suffix?: string;
  tone?: "neutral" | "primary" | "amber";
}) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card px-4 py-3.5 shadow-sm sm:px-5 sm:py-4">
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "size-1.5 rounded-full",
            tone === "primary" && "bg-primary",
            tone === "amber" && "bg-amber-500",
            tone === "neutral" && "bg-muted-foreground/40",
          )}
        />
        <p className="text-xs font-medium text-muted-foreground sm:text-sm">
          {label}
        </p>
      </div>
      <p
        className="mt-1.5 text-2xl font-semibold leading-none tabular-nums text-foreground sm:mt-2 sm:text-3xl"
        style={{ fontFamily: "var(--font-heading)" }}
      >
        {value}
        {suffix && (
          <span className="ml-1 text-sm font-normal text-muted-foreground">
            {suffix}
          </span>
        )}
      </p>
    </div>
  );
}
